import { z } from "zod/v4-mini";

import { Schema, cz } from "@/utils/zodUtils.ts";

export const deviceTypeSchema = z.literal(["pump", "pen", "reader", "sensor", "manual"]);
export type DeviceType = z.infer<typeof deviceTypeSchema>;

const pumpDeviceBrandList = [
  "omnipod 5",
  "omnipod dash",
  "minimed 780g",
  "minimed 740g",
  "minimed 640g",
  "minimed",
  "tandem slim x2 control iq",
  "tandem slim x2",
  "unknown",
] as const;

export const pumpDeviceBrandSchema = z.literal(pumpDeviceBrandList, "form.required");

export type PumpDeviceBrand = z.infer<typeof pumpDeviceBrandSchema>;

// Selectable pump brands, in display order (single source for the schema and the UI).
export const pumpDeviceBrands: readonly PumpDeviceBrand[] = pumpDeviceBrandList;

export type InsulinPumpProfileSegment = {
  id: string;
  start: string;
  end: string;
  basalRate: number;
  insulinToCarbRatio: number;
  sensitivity: number;
  bloodGlucoseThreshold?: number;
  targetBloodGlucose?: number;
};

export type InsulinPumpProfile = {
  id: string;
  name: string;
  activeInsulinTime?: number;
  basalInsulinPerDay?: number;
  currentProfile: boolean;

  segments: InsulinPumpProfileSegment[];
};

export type InsulinPump = {
  profiles: InsulinPumpProfile[];
  parametersFile?: string;
};

export type MedicalDevice = {
  id: string;
  serialNumber?: string;
  name: string;
  displayName?: string;
  manufacturer: string;
  typeId: string;
  kind: DeviceType;
  lastUpload?: string;
  insulinPump?: InsulinPump;
};

export const insulinPumpProfileSegmentSchema: Schema<InsulinPumpProfileSegment> = z.pipe(
  cz.camelCaseObject({
    id: cz.id(),
    start_time: z.iso.time(),
    end_time: z.iso.time(),
    basal_rate: z.number(),
    insulin_to_carb_ratio: z.number(),
    sensitivity: z.number(),
    blood_glucose_threshold: cz.optional(z.number()),
    target_blood_glucose: cz.optional(z.number()),
  }),
  z.transform(data => ({
    ...data,
    start: data.startTime,
    end: data.endTime,
  })),
);

export const insulinPumpProfileSchema: Schema<InsulinPumpProfile> = cz.camelCaseObject({
  id: cz.id(),
  name: z.string(),
  active_insulin_time: cz.optional(z.number()),
  basal_insulin_per_day: cz.optional(z.number()),
  current_profile: z.boolean(),
  segments: cz.forcedArray(insulinPumpProfileSegmentSchema),
});

export const insulinPumpSchema: Schema<InsulinPump> = cz.camelCaseObject({
  profiles: cz.forcedArray(insulinPumpProfileSchema),
  parameters_file: cz.optional(z.url()),
});

export const medicalDeviceSchema: Schema<MedicalDevice> = z.pipe(
  cz.camelCaseObject({
    id: cz.id(),
    serial_number: cz.optional(z.string()),
    display_name: cz.optional(z.string()),
    device_type: cz.camelCaseObject({
      id: cz.id(),
      name: z.string(),
      manufacturer: z.string(),
      kind: deviceTypeSchema,
    }),
    last_upload: cz.optional(cz.datetime()),
    insulin_pump: cz.optional(insulinPumpSchema),
  }),
  z.transform(data => ({
    ...data,
    typeId: data.deviceType.id,
    kind: data.deviceType.kind,
    manufacturer: data.deviceType.manufacturer,
    name: data.deviceType.name,
  })),
);

// Chart-tooltip labels for the device that recorded a data point.
export const deviceTypeInputMethodTranslationMapping = (deviceType: DeviceType): string => {
  switch (deviceType) {
    case "pump":
      return "charts.inputMethod.pump";
    case "pen":
      return "charts.inputMethod.pen";
    case "reader":
      return "charts.inputMethod.reader";
    case "sensor":
      return "charts.inputMethod.sensor";
    case "manual":
      return "charts.inputMethod.manual";
  }
};

export const pumpModelTranslationMapping = (brand: PumpDeviceBrand | undefined): string => {
  if (!brand) {
    return "models.pumpModel.unknown";
  }
  switch (brand) {
    case "minimed":
      return "models.pumpModel.minimed";
    case "minimed 640g":
      return "models.pumpModel.minimed640g";
    case "minimed 740g":
      return "models.pumpModel.minimed740g";
    case "minimed 780g":
      return "models.pumpModel.minimed780g";
    case "omnipod dash":
      return "models.pumpModel.omnipodDash";
    case "omnipod 5":
      return "models.pumpModel.omnipod5";
    case "tandem slim x2":
      return "models.pumpModel.tandemSlimX2";
    case "tandem slim x2 control iq":
      return "models.pumpModel.tandemSlimX2ControlIQ";
    case "unknown":
      return "models.pumpModel.unknown";
  }
};
